import React, { useState, useEffect } from "react";
import { RightMinimalArrow } from "icons";
import { motion } from "framer-motion";
import classnames from "classnames";
import PropTypes from "prop-types";

import Collapse from "./Collapse";
import Typography from "./Typography";
import Tooltip from "./Tooltip";

const ACCORDION_STYLES = {
  primary: "primary",
  secondary: "secondary",
};

const Accordion = ({
  children,
  defaultActiveKey = undefined,
  multipleExpanded = false,
  padded = false,
  style = "primary",
  className = "",
}) => {
  const [openTab, setOpenTab] = useState([defaultActiveKey]);

  const addToOpenTabList = index => {
    openTab ? setOpenTab([...openTab, index]) : setOpenTab([index]);
  };

  const removeFromOpenTabList = index => {
    const position = openTab.indexOf(index);
    let openTabTemp = [...openTab];
    openTabTemp.splice(position, 1);
    setOpenTab(openTabTemp);
  };

  useEffect(() => {
    typeof defaultActiveKey === "number"
      ? setOpenTab([defaultActiveKey])
      : setOpenTab(defaultActiveKey);
  }, [defaultActiveKey]);

  return (
    <div
      className={classnames({
        "p-6": padded,
        "bg-gray-100": style === ACCORDION_STYLES.secondary,
        [className]: className,
      })}
    >
      {React.Children.map(children, (child, index) => {
        const isSingleOrLastChild =
          !children.length || index === children.length - 1;
        return React.cloneElement(child, {
          id: index,
          key: index,
          isOpen: openTab?.includes(index),
          className: classnames(child.props.className, {
            "cs-ui-accordion__wrapper--last-item": isSingleOrLastChild,
          }),
          onClick: () => {
            if (openTab?.includes(index)) {
              multipleExpanded
                ? removeFromOpenTabList(index)
                : setOpenTab(undefined);
            } else {
              multipleExpanded ? addToOpenTabList(index) : setOpenTab([index]);
            }
            child.props.onClick && child.props.onClick();
          },
        });
      })}
    </div>
  );
};

export const Item = ({
  id,
  title,
  isOpen = false,
  onClick,
  children,
  className,
  titleProps = {},
  iconProps = {},
  iconTextOpened,
  iconTextClosed,
  actionProps = { className: "" },
  isDisabled = false,
  tooltipContent,
}) => {
  const { className: actionClassName, ...otherActionProps } = actionProps;

  const AccordionItem = () => (
    <div
      className={classnames("cs-ui-accordion__wrapper", {
        [className]: className,
      })}
    >
      <div
        tabIndex={0}
        role="button"
        onClick={onClick}
        id={`cs-ui-accordion-item-${id}`}
        aria-disabled={isOpen}
        aria-expanded={isOpen}
        aria-controls={`cs-ui-accordion-section-${id}`}
        className={classnames("cs-ui-accordion__item", {
          "cs-ui-accordion__item--open": isOpen && !isDisabled,
        })}
        onKeyDown={e => {
          switch (e.key) {
            case " ":
            case "Enter":
              onClick();
              break;
            default:
          }
        }}
      >
        <div
          {...titleProps}
          className={classnames(
            "cs-ui-accordion__item-handle flex-grow-1 flex items-center truncate",
            {
              "text-gray-300 dark:text-gray-500 cursor-not-allowed": isDisabled,
              "cs-ui-text-gray-800": isOpen && !isDisabled,
            }
          )}
        >
          <div className="truncate">{title}</div>
        </div>

        <div
          className={classnames("flex items-center", actionClassName)}
          {...otherActionProps}
        >
          {!isOpen && iconTextClosed && (
            <Typography className="text-sm text-gray-400">
              {iconTextClosed}
            </Typography>
          )}
          {isOpen && iconTextOpened && (
            <Typography className="text-sm text-gray-400">
              {iconTextOpened}
            </Typography>
          )}
          <motion.div
            variants={{ open: { rotate: 270 }, collapsed: { rotate: 90 } }}
            animate={isOpen && !isDisabled ? "open" : "collapsed"}
            transition={{ duration: 0.3 }}
            className="flex-grow-0 ml-2 cs-ui-accordion__item-toggle-icon"
          >
            <RightMinimalArrow size={16} color="#68737D" {...iconProps} />
          </motion.div>
        </div>
      </div>
      <Collapse
        role="region"
        open={!isDisabled && isOpen}
        className="cs-ui-accordion__drop"
        id={`cs-ui-accordion-section-${id}`}
        aria-labelledby={`cs-ui-accordion-item-${id}`}
      >
        {children}
      </Collapse>
    </div>
  );

  return tooltipContent ? (
    <Tooltip content={tooltipContent} position="top">
      {AccordionItem()}
    </Tooltip>
  ) : (
    AccordionItem()
  );
};

Accordion.defaultProps = {
  padded: false,
  style: "primary",
};

Item.displayName = "Accordion.Item";

Item.propTypes = {
  id: PropTypes.number,
  /**
   * Title of the Accordion
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClick: PropTypes.func,
  children: PropTypes.node,
  /**
   * Props passed to Accordion title
   */
  titleProps: PropTypes.object,
  /**
   * Props passed to Accordion toggle icon
   */
  iconProps: PropTypes.object,
};

Accordion.propTypes = {
  children: PropTypes.node,
  style: PropTypes.oneOf(Object.values(ACCORDION_STYLES)),
  /**
   * Add padding to the Accordion container
   */
  padded: PropTypes.bool,
  /**
   * Index of the Accordion item to be opened initially
   */
  defaultActiveKey: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  /**
   * Custom class applied to Accordion container
   */
  className: PropTypes.string,
};

Accordion.Item = Item;

export default Accordion;
